import React, { useMemo } from 'react'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useAxesTooltip } from '@mui/x-charts/ChartsTooltip'
import { useTranslation } from 'react-i18next'
import Table from '@mui/material/Table'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import { useXAxis } from '@mui/x-charts/hooks'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatNumber,
} from '../../../../packages/utils/numberFormatters'
import { ColoredPercentage } from '../../../../components/entities/utils/ColoredPercentage'
import { isAfter, isSameDay } from 'date-fns'
import { TradesType } from '../../../../api/InstrumentApi'

export const TradesGraphTooltip = ({ currency, trades }: { currency: string | null; trades: TradesType | null }) => {
    const [{ dataIndex } = {}] = useAxesTooltip() || []
    const { i18n } = useTranslation()
    const { data: xAxisData } = useXAxis()

    const { shareTrades, unitTrades, rightTrades } = useMemo(() => {
        if (!trades) return { shareTrades: {}, unitTrades: {}, rightTrades: {} }

        const shareTrades: Record<string, TradesType['share'][number]> = {}

        for (let i = 0; i < trades.share.length; i++) {
            const trade = trades.share[i]
            shareTrades[formatDateYYYYMMDD(trade.recordDate)] = trade
        }

        const unitTrades: Record<string, TradesType['unit'][number][number]> = {}
        const units = Object.values(trades.unit)

        for (let i = 0; i < units.length; i++) {
            for (let j = 0; j < units[i].length; j++) {
                const trade = units[i][j]
                const formattedDate = formatDateYYYYMMDD(trade.recordDate)

                if (!unitTrades[formattedDate]?.closePrice) {
                    unitTrades[formattedDate] = trade
                }
            }
        }

        const rightTrades: Record<string, TradesType['right'][number][number]> = {}
        const rights = Object.values(trades.right)

        for (let i = 0; i < rights.length; i++) {
            for (let j = 0; j < rights[i].length; j++) {
                const trade = rights[i][j]
                const formattedDate = formatDateYYYYMMDD(trade.recordDate)

                if (!rightTrades[formattedDate]?.closePrice) {
                    rightTrades[formattedDate] = trade
                }
            }
        }

        return { shareTrades, unitTrades, rightTrades }
    }, [trades])

    if (!dataIndex) {
        return null
    }

    const currentDay = new Date(xAxisData?.[dataIndex])
    const currentDayFormatted = formatDateYYYYMMDD(currentDay)
    const currentTrade = shareTrades[currentDayFormatted]
    if (!currentTrade) return null

    const firstDate = xAxisData?.[0]
    const firstTradeClosePrice =
        trades?.share.find(
            (t) => t.closePrice !== null && (isSameDay(t.recordDate, firstDate) || isAfter(t.recordDate, firstDate)),
        )?.closePrice ?? null
    const currentRight = rightTrades[currentDayFormatted]
    const currentUnit = unitTrades[currentDayFormatted]
    const currentUnitOrRight = currentRight?.closePrice ? currentRight : currentUnit?.closePrice ? currentUnit : null

    return (
        <Paper
            elevation={0}
            sx={{
                m: 1,
                p: 1.5,
                border: 'solid',
                borderWidth: 2,
                borderColor: 'divider',
            }}
        >
            <Table sx={{ td: { border: 0, paddingX: 2, paddingY: 1 } }}>
                <TableBody>
                    <TableRow>
                        <TableCell colSpan={2}>
                            <Typography fontWeight={'bold'}>
                                {formatDateYYYYMMDD(currentDay)}{' '}
                                {['sön', 'mån', 'tis', 'ons', 'tor', 'fre', 'lör'][currentDay.getDay()]}
                            </Typography>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell>Mcap</TableCell>
                        <TableCell>
                            {currentTrade.marketcap === null
                                ? '-'
                                : formatBigMoney(currentTrade.marketcap, {
                                      currencySymbol: currency,
                                      locale: i18n.language,
                                  })}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>Close</TableCell>
                        <TableCell>
                            {currentTrade.closePrice === null
                                ? '-'
                                : formatMoney(currentTrade.closePrice, { locale: i18n.language })}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>Utv</TableCell>
                        <TableCell>
                            {(currentTrade.closePrice === null || firstTradeClosePrice === null) && '-'}
                            {currentTrade.closePrice !== null && firstTradeClosePrice !== null && (
                                <ColoredPercentage
                                    value={100 * (+currentTrade.closePrice / +firstTradeClosePrice - 1)}
                                />
                            )}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>VWAP</TableCell>
                        <TableCell>
                            {currentTrade.vwap === null
                                ? '-'
                                : formatMoney(currentTrade.vwap, { locale: i18n.language })}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>Oms</TableCell>
                        <TableCell>
                            {formatBigMoney(currentTrade.turnover === null ? 0 : +currentTrade.turnover, {
                                currencySymbol: currency,
                                locale: i18n.language,
                            })}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ 'td&': { paddingBottom: currentUnitOrRight ? 2 : undefined } }}>
                            Volym
                        </TableCell>
                        <TableCell sx={{ 'td&': { paddingBottom: currentUnitOrRight ? 2 : undefined } }}>
                            {formatNumber(currentTrade.quantity ?? 0, { decimals: 0, locale: i18n.language })} st
                        </TableCell>
                    </TableRow>
                    {currentUnitOrRight && (
                        <>
                            <TableRow sx={{ borderTop: 1, borderColor: 'divider' }}>
                                <TableCell sx={{ 'td&': { fontWeight: 'bold', paddingTop: 2 } }}>
                                    {currentUnitOrRight.type === 'unit' ? 'BTA/BTU' : 'Rätter'}
                                </TableCell>
                                <TableCell />
                            </TableRow>
                            <TableRow>
                                <TableCell>VWAP</TableCell>
                                <TableCell>
                                    {formatMoney(currentUnitOrRight.rawVwap, { locale: i18n.language })}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Volym</TableCell>
                                <TableCell>
                                    {formatMoney(currentUnitOrRight.rawQuantity, { locale: i18n.language })}
                                </TableCell>
                            </TableRow>
                        </>
                    )}
                </TableBody>
            </Table>
        </Paper>
    )
}
